import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { ExternalLink } from 'react-feather';
import { GetTicketNumberSequenceQuery as GetTicketNumberSequence } from '__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../common/helpers';
import UI from '../../../common/UI';
import useEditTicketNumberSequenceMutation from './useEditTicketNumberSequenceMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type TicketNumberSequence = GetTicketNumberSequence['project']['ticket_number_sequence'];

const editManualTicketNumbersSchema = yup.object().shape({});

interface EditManualTicketNumbersFormProps {
  sequence: TicketNumberSequence;
}

const EditManualTicketNumbersForm = ({ sequence }: EditManualTicketNumbersFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (sequence: TicketNumberSequence) => ({
    manual_ticket_numbers: sequence.manual_ticket_numbers,
  });

  const form = useForm({
    values: getValues(sequence),
  });

  useNavigationPrompt(form.changed());

  const [editTicketNumberSequence, { error, loading }] = useEditTicketNumberSequenceMutation({
    input: {
      id: sequence.id,
      manual_entry: {
        ticket_numbers: form.values.manual_ticket_numbers,
      },
    },
    onSuccess: (sequence) => {
      form.reset(getValues(sequence));
    },
  });

  const { valid, errors } = useValidation({
    schema: editManualTicketNumbersSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editTicketNumberSequence()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('manual_entry')}</UI.Legend>
          <UI.InputDescription>
            {t('manual_ticket_numbers_description')}
            {' '}
            <UI.A
              href="https://about.atleta.cc/article/assigning-ticket-numbers-manually"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('check_the_manual')}
              {' '}
              <UI.Icon>
                <ExternalLink />
              </UI.Icon>
            </UI.A>
          </UI.InputDescription>
        </UI.Div>
        <UI.InputGroup
          sc={{ valid: !errors.manual_ticket_numbers, touched: form.touched.manual_ticket_numbers }}
        >
          <UI.InputLabel htmlFor="ManualTicketNumbers">
            {t('manual_ticket_numbers')}
          </UI.InputLabel>
          <UI.TextArea
            {...form.textarea('manual_ticket_numbers')}
            id="ManualTicketNumbers"
            style={{ fontFamily: 'monospace' }}
            rows={20}
          />
          <UI.ErrorMessages
            attribute={t('manual_ticket_numbers')}
            errors={errors.manual_ticket_numbers}
          />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket_number_sequence')}
          closeTo={`/${project.id}/participants/bib-numbers?${encodeUrl({ event: sequence.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditManualTicketNumbersForm;
